import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useSearch } from "@tanstack/react-router";
import { useAuth } from "@/auth/AuthProvider";
import type { AuthUser } from "@/api/types";
import { HeaderSwitcher } from "@/components/shared";
import type { EntranceMode } from "@/routes";

export function Header() {
  const { isAuthenticated, user, isLoading, logout } = useAuth();
  const activeMode = useActiveEntranceMode();

  return (
    <header className="border-b border-rule bg-surface-card">
      <div className="container mx-auto flex h-16 items-center justify-between gap-2 px-3 sm:gap-6 sm:px-4">
        <Link
          to="/"
          className="flex shrink-0 items-center gap-2 rounded text-ui font-semibold text-ink-primary transition-colors hover:text-ink-info"
        >
          <img src="/Logo.png" alt="" aria-hidden className="h-7 w-7 rounded-sm" />
          {/* Wordmark text is the first thing to yield on a narrow phone — the
              logo still anchors the home link. */}
          <span className="hidden font-serif text-section tracking-tight sm:inline">QuarterMill</span>
        </Link>
        <HeaderSwitcher active={activeMode} />
        <nav className="flex items-center gap-2" aria-label="Primary">
          {isLoading ? null : isAuthenticated && user ? (
            <>
              {user.org_id && (
                <Link
                  to="/upload"
                  className="rounded px-3 py-1.5 text-meta font-medium text-ink-secondary transition-colors hover:text-ink-primary"
                >
                  Upload
                </Link>
              )}
              <UserMenu user={user} onLogout={logout} />
            </>
          ) : (
            <AnonNav />
          )}
        </nav>
      </div>
    </header>
  );
}

/** Resolves which of the three entrances (Query / Graph / Catalog) should
 * read as active. On `/` we derive from the `?mode=` search param, falling
 * back to `"graph"` because that's the homepage default. On every other
 * route the user is past the entrance step, so we return `null` — the
 * switcher renders all three pills in their inactive state, neither
 * lying nor hiding the way back. */
function useActiveEntranceMode(): EntranceMode | null {
  const pathname = useLocation({ select: (loc) => loc.pathname });
  const indexSearch = useSearch({ strict: false }) as { mode?: EntranceMode };
  if (pathname !== "/") return null;
  return indexSearch.mode ?? "graph";
}

function AnonNav() {
  return (
    <>
      <Link
        to="/login"
        className="rounded px-3 py-1.5 text-meta font-medium text-ink-secondary transition-colors hover:text-ink-primary"
      >
        Log in
      </Link>
      <Link
        to="/register"
        className="rounded-card bg-ink-primary px-3 py-1.5 text-meta font-medium text-surface-card transition hover:bg-ink-info"
      >
        Sign up
      </Link>
    </>
  );
}

function UserMenu({ user, onLogout }: { user: AuthUser; onLogout: () => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function handleKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    window.addEventListener("mousedown", handleClick);
    window.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("mousedown", handleClick);
      window.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const initial = user.email.charAt(0).toUpperCase();
  const showOrgLink = user.role === "org_admin" || user.role === "admin";
  const roleLabel =
    user.role === "org_admin"
      ? "Organization admin"
      : user.role === "admin"
        ? "Admin"
        : "Member";

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu for ${user.email}`}
        // 44px hit area (touch target) wrapping a 36px visual avatar, so the
        // circle stays in scale with the text nav links beside it.
        className="group flex h-11 w-11 items-center justify-center rounded-full"
      >
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-surface-sunken text-meta font-semibold text-ink-primary transition-colors group-hover:bg-rule/40">
          {initial}
        </span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-[min(16rem,calc(100vw-1.5rem))] overflow-hidden rounded-card border border-rule bg-surface-card shadow-lg"
        >
          <div className="border-b border-rule px-4 py-3">
            <p className="truncate text-meta font-medium text-ink-primary">
              {user.email}
            </p>
            <p className="text-attribution text-ink-tertiary">
              {roleLabel}
              {user.org_id ? " · in an organization" : " · unaffiliated"}
            </p>
          </div>
          <div className="py-1">
            <Link
              to="/profile"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="block px-4 py-2 text-meta text-ink-secondary transition-colors hover:bg-surface-sunken hover:text-ink-primary"
            >
              Profile
            </Link>
            {showOrgLink && (
              <Link
                to="/org"
                role="menuitem"
                onClick={() => setOpen(false)}
                className="block px-4 py-2 text-meta text-ink-secondary transition-colors hover:bg-surface-sunken hover:text-ink-primary"
              >
                Organization
              </Link>
            )}
          </div>
          <div className="border-t border-rule py-1">
            <button
              type="button"
              role="menuitem"
              onClick={async () => {
                setOpen(false);
                await onLogout();
              }}
              className="block w-full px-4 py-2 text-left text-meta text-ink-secondary transition-colors hover:bg-surface-sunken hover:text-ink-primary"
            >
              Log out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
